import { useState, useEffect } from 'react';
import { getAdminUsers, createAdminUser, updateAdminUser, deleteAdminUser, resetAdminPassword } from '../utils/api';
import { closeModalWithAnimation } from '../utils/modalHelpers';
import AdminPageSkeleton from '../components/AdminPageSkeleton';
import ConfirmModal from '../components/ConfirmModal';
import Error500 from './errors/Error500';
import './AdminPages.css';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverError, setServerError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingUser, setEditingUser] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showColorModal, setShowColorModal] = useState(false);
  const [colorModalUser, setColorModalUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [openActionId, setOpenActionId] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({
    top: 0,
    left: 0,
    transform: 'translateX(-100%)'
  });
  
  const [formData, setFormData] = useState({
    meno: '',
    cele_meno: '',
    email: '',
    heslo: '',
    rola: 'admin',
    aktivny: true
  });

  const [resetPasswordData, setResetPasswordData] = useState({
    heslo: '',
    heslo_confirm: ''
  });
  
  const [modalError, setModalError] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');

  const getInitials = (fullName) => {
    if (!fullName) return '??';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  };

  const formatDateTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return {
      date: date.toLocaleDateString('sk-SK'),
      time: date.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' })
    };
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.actions-dropdown')) {
        setOpenActionId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!openActionId) return;
    const handleClose = () => setOpenActionId(null);
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);
    return () => {
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [openActionId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await getAdminUsers();
      setUsers(response.data.data || []);
      setError(null);
      setServerError(false);
    } catch (err) {
      console.error('Failed to load users:', err);
      if (!err.response || err.response.status >= 500 || err.code === 'ERR_NETWORK') {
        setServerError(true);
        return;
      }
      setError('Nepodarilo sa načítať administrátorov');
    } finally {
      setLoading(false);
    }
  };

  const currentAdmin = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isSuperAdmin = currentAdmin.rola === 'super_admin';

  const visibleUsers = users.filter(u =>
    u.can_edit || u.can_delete || u.id === currentAdmin.id
  );

  const counts = {
    all: visibleUsers.length,
    active: visibleUsers.filter(u => u.aktivny).length,
    inactive: visibleUsers.filter(u => !u.aktivny).length
  };

  const filteredUsers = visibleUsers.filter(user => {
    const matchesSearch = user.meno.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || (filter === 'active' && user.aktivny) || (filter === 'inactive' && !user.aktivny);
    return matchesSearch && matchesFilter;
  });

  const handleAdd = () => {
    setModalMode('add');
    setEditingUser(null);
    setFormData({
      meno: '',
      cele_meno: '',
      email: '',
      heslo: '',
      rola: 'admin',
      aktivny: true
    });
    setModalError('');
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setModalMode('edit');
    setEditingUser(user);
    setFormData({
      meno: user.meno,
      cele_meno: user.cele_meno || '',
      email: user.email,
      heslo: '',
      rola: user.rola,
      aktivny: user.aktivny
    });
    setModalError('');
    setShowModal(true);
  };

  const handleResetPassword = (user) => {
    setEditingUser(user);
    setResetPasswordData({ heslo: '', heslo_confirm: '' });
    setResetPasswordError('');
    setShowResetPasswordModal(true);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowConfirmDelete(true);
  };

  const handleChangeColor = (user) => {
    setColorModalUser(user);
    setShowColorModal(true);
  };

  const handleColorSelect = async (colorIndex) => {
    if (!colorModalUser) return;
    
    try {
      await updateAdminUser(colorModalUser.id, { avatar_color: colorIndex });
      await loadUsers();
      closeModalWithAnimation(() => setShowColorModal(false));
      setColorModalUser(null);
    } catch (err) {
      console.error('Color update error:', err);
      alert('Vyskytla sa chyba pri zmene farby');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalError('');

    try {
      if (modalMode === 'add') {
        await createAdminUser(formData);
      } else {
        const updateData = {
          meno: formData.meno,
          cele_meno: formData.cele_meno,
          email: formData.email,
          aktivny: formData.aktivny
        };
        await updateAdminUser(editingUser.id, updateData);
      }
      
      await loadUsers();
      closeModalWithAnimation(() => setShowModal(false));
    } catch (err) {
      console.error('Submit error:', err);
      if (err.response?.data?.message) {
        setModalError(err.response.data.message);
      } else {
        setModalError('Vyskytla sa chyba pri ukladaní');
      }
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setResetPasswordError('');

    if (resetPasswordData.heslo !== resetPasswordData.heslo_confirm) {
      setResetPasswordError('Heslá sa nezhodujú');
      return;
    }

    if (resetPasswordData.heslo.length < 8) {
      setResetPasswordError('Heslo musí mať aspoň 8 znakov');
      return;
    }

    try {
      await resetAdminPassword(editingUser.id, { heslo: resetPasswordData.heslo });
      await loadUsers();
      closeModalWithAnimation(() => setShowResetPasswordModal(false));
    } catch (err) {
      console.error('Reset password error:', err);
      if (err.response?.data?.message) {
        setResetPasswordError(err.response.data.message);
      } else {
        setResetPasswordError('Vyskytla sa chyba pri zmene hesla');
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteAdminUser(userToDelete.id);
      await loadUsers();
      setShowConfirmDelete(false);
      setUserToDelete(null);
    } catch (err) {
      console.error('Delete error:', err);
      alert(err.response?.data?.message || 'Vyskytla sa chyba pri mazaní');
    }
  };

  const toggleStatus = async (user) => {
    try {
      await updateAdminUser(user.id, { aktivny: !user.aktivny });
      await loadUsers();
    } catch (err) {
      console.error('Toggle status error:', err);
      alert(err.response?.data?.message || 'Vyskytla sa chyba');
    }
  };

  if (serverError) {
    return <Error500 />;
  }

  if (loading) {
    return (
      <AdminPageSkeleton
        title="Administrátori"
        subtitle="Správa administrátorských účtov a oprávnení"
        variant="users"
        showAction={isSuperAdmin}
      />
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>Administrátori</h1>
          <p className="text-muted">Správa administrátorskych účtov a oprávnení</p>
        </div>
        <div className="header-actions">
          {isSuperAdmin && (
            <button onClick={handleAdd} className="btn btn-primary btn-lg">Pridať administrátora</button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      <div className="filters-section">
        <div className="search-box">
          <svg className="search-icon" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Hľadať podľa mena alebo emailu..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-tabs">
          <button 
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Všetci
            <span className="tab-count">{counts.all}</span>
          </button>
          <button 
            className={`filter-tab ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Aktívni
            <span className="tab-count">{counts.active}</span>
          </button>
          <button 
            className={`filter-tab ${filter === 'inactive' ? 'active' : ''}`}
            onClick={() => setFilter('inactive')}
          >
            Neaktívni
            <span className="tab-count">{counts.inactive}</span>
          </button>
        </div>
      </div>

      <div className="table-container">
        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <p>Žiadni administrátori</p>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Meno a email</th>
                  <th>Login</th>
                  <th>Rola</th>
                  <th>Stav</th>
                  <th>Vytvoril</th>
                  <th>Vytvorený</th>
                  <th>Posledné prihlásenie</th>
                  <th>Akcie</th>
                </tr>
              </thead>
              <tbody>
              {filteredUsers.map((user) => {
                const getInitials = (fullName) => {
                  if (!fullName) return '??';
                  const parts = fullName.trim().split(/\s+/);
                  if (parts.length >= 2) {
                    return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase();
                  }
                  return fullName.substring(0, 2).toUpperCase();
                };
                
                const getGradientClass = (user) => {
                  const colorIndex = user.avatar_color !== null && user.avatar_color !== undefined 
                    ? user.avatar_color 
                    : user.id % 10;
                  return `admin-avatar-gradient-${colorIndex}`;
                };
                
                return (
                <tr key={user.id}>
                  <td className="td-center">
                    <div className="user-avatar-wrapper">
                      <div className={`admin-avatar ${getGradientClass(user)}`}>
                        {getInitials(user.cele_meno)}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="customer-info">
                      <div>
                        <div className="customer-name">
                          <strong>{user.cele_meno || user.meno}</strong>
                        </div>
                        <span className="customer-email">
                          {user.email}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="text-muted">{user.meno}</span>
                  </td>
                  <td>
                    <span className={`badge ${user.rola === 'super_admin' ? 'badge-role-super' : 'badge-role-admin'}`}>
                      {user.rola === 'super_admin' ? 'ŠÉF' : 'PRACOVNÍK'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${user.aktivny ? 'badge-success' : 'badge-danger'}`}>
                      {user.aktivny ? 'AKTIVOVANÝ' : 'DEAKTIVOVANÝ'}
                    </span>
                  </td>
                  <td className="text-muted"> {user.vytvoril_meno || 'N/A'} </td>
                  <td className="td-center">
                    {(() => {
                      const createdAt = formatDateTime(user.vytvorene);
                      if (!createdAt) {
                        return <span className="text-muted">—</span>;
                      }
                      return (
                        <div className="datetime-info">
                          <strong>{createdAt.date}</strong>
                          <span className="time-info">{createdAt.time}</span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="td-center">
                    {(() => {
                      const lastLogin = formatDateTime(user.posledne_prihlasenie);
                      if (!lastLogin) {
                        return <span className="text-muted">—</span>;
                      }
                      return (
                        <div className="datetime-info">
                          <strong>{lastLogin.date}</strong>
                          <span className="time-info">{lastLogin.time}</span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="td-center">
                    {(user.can_edit || user.can_delete || user.id === currentAdmin.id) && (
                    <div className="actions-dropdown">
                      <button
                        className="btn-icon"
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={openActionId === user.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          const rect = event.currentTarget.getBoundingClientRect();
                          const menuWidth = 220;
                          const menuHeight = 260;
                          const viewportWidth = window.innerWidth;
                          const viewportHeight = window.innerHeight;
                          const availableRight = viewportWidth - rect.right;
                          const availableBottom = viewportHeight - rect.bottom;
                          const availableTop = rect.top;
                          const shouldFlipLeft = availableRight < menuWidth + 12;
                          const shouldFlipUp = availableBottom < menuHeight + 12 && availableTop >= menuHeight + 12;
                          const translateX = shouldFlipLeft ? '-100%' : '0';
                          const translateY = shouldFlipUp ? '-100%' : '0';
                          setActionMenuPosition({
                            top: shouldFlipUp ? rect.top - 8 : rect.bottom + 8,
                            left: shouldFlipLeft ? rect.right : rect.left,
                            transform: `translate(${translateX}, ${translateY})`
                          });
                          setOpenActionId(prev => prev === user.id ? null : user.id);
                        }}
                        title="Akcie"
                      >
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M3 9.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/>
                        </svg>
                      </button>
                      {openActionId === user.id && (
                        <div
                          className="dropdown-menu dropdown-menu-fixed"
                          role="menu"
                          style={{
                            top: `${actionMenuPosition.top}px`,
                            left: `${actionMenuPosition.left}px`,
                            transform: actionMenuPosition.transform
                          }}
                        >
                          {(user.can_edit || user.id === currentAdmin.id) && (
                            <>
                              <button
                                className="dropdown-item action-edit"
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleEdit(user);
                                  setOpenActionId(null);
                                }}
                              >
                                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M20.9888 4.28491L19.6405 2.93089C18.4045 1.6897 16.4944 1.6897 15.2584 2.93089L13.0112 5.30042L18.7416 11.055L21.1011 8.68547C21.6629 8.1213 22 7.33145 22 6.54161C22 5.75176 21.5506 4.84908 20.9888 4.28491Z"/>
                                  <path d="M16.2697 10.9422L11.7753 6.42877L2.89888 15.3427C2.33708 15.9069 2 16.6968 2 17.5994V21.0973C2 21.5487 2.33708 22 2.89888 22H6.49438C7.2809 22 8.06742 21.6615 8.74157 21.0973L17.618 12.1834L16.2697 10.9422Z"/>
                                </svg>
                                Upraviť
                              </button>
                              {user.id !== currentAdmin.id && (
                                <button
                                  className={`dropdown-item ${user.aktivny ? 'action-deactivate' : 'action-activate'}`}
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleStatus(user);
                                    setOpenActionId(null);
                                  }}
                                >
                                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                    {user.aktivny ? (
                                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z M5 6.5A1.5 1.5 0 0 1 6.5 5h3A1.5 1.5 0 0 1 11 6.5v3A1.5 1.5 0 0 1 9.5 11h-3A1.5 1.5 0 0 1 5 9.5v-3z"/>
                                    ) : (
                                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
                                    )}
                                  </svg>
                                  {user.aktivny ? 'Deaktivovať' : 'Aktivovať'}
                                </button>
                              )}
                              <button
                                className="dropdown-item action-reset"
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleResetPassword(user);
                                  setOpenActionId(null);
                                }}
                              >
                                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                  <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
                                </svg>
                                Reset hesla
                              </button>
                            </>
                          )}
                          {user.id === currentAdmin.id && (
                            <button
                              className="dropdown-item action-color"
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleChangeColor(user);
                                setOpenActionId(null);
                              }}
                            >
                              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M13.5 1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.499 2.499 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5zm-8.5 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm11 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"/>
                              </svg>
                              Zmeniť farbu avatara
                            </button>
                          )}
                          {user.can_delete && user.id !== currentAdmin.id && (
                            <button
                              className="dropdown-item action-delete"
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDeleteClick(user);
                                setOpenActionId(null);
                              }}
                            >
                              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z"/>
                                <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3h11V2h-11v1Z"/>
                              </svg>
                              Zmazať
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          
          {/* Mobile Cards View */}
          <div className="user-mobile-cards">
            {filteredUsers.map(user => {
              const getGradientClass = (user) => {
                const colorIndex = user.avatar_color !== null && user.avatar_color !== undefined 
                  ? user.avatar_color 
                  : user.id % 10;
                return `admin-avatar-gradient-${colorIndex}`;
              };
              
              return (
              <div key={user.id} className={`user-mobile-card ${!user.aktivny ? 'inactive' : ''}`}>
                <div className="user-mobile-header">
                  <div className={`admin-avatar ${getGradientClass(user)}`}>
                    {getInitials(user.cele_meno || user.prihlasovacie_meno)}
                  </div>
                  <div className="user-mobile-info">
                    <div className="user-mobile-name">{user.cele_meno || user.prihlasovacie_meno}</div>
                    <div className="user-mobile-email">{user.email}</div>
                  </div>
                </div>
                
                <div className="user-mobile-details">
                  <div className="user-mobile-detail">
                    <span className="user-mobile-detail-label">Rola</span>
                    <span className="user-mobile-detail-value">
                      <span className={`badge ${user.rola === 'super_admin' ? 'badge-role-super' : 'badge-role-admin'}`}>
                        {user.rola === 'super_admin' ? 'ŠÉF' : 'PRACOVNÍK'}
                      </span>
                    </span>
                  </div>
                  <div className="user-mobile-detail">
                    <span className="user-mobile-detail-label">Stav</span>
                    <span className="user-mobile-detail-value">
                      <span className={`badge ${user.aktivny ? 'badge-success' : 'badge-danger'}`}>
                        {user.aktivny ? 'AKTIVOVANÝ' : 'DEAKTIVOVANÝ'}
                      </span>
                    </span>
                  </div>
                  <div className="user-mobile-detail">
                    <span className="user-mobile-detail-label">Vytvoril</span>
                    <span className="user-mobile-detail-value">{user.vytvoril_meno || 'N/A'}</span>
                  </div>
                  <div className="user-mobile-detail">
                    <span className="user-mobile-detail-label">Vytvorený</span>
                    <span className="user-mobile-detail-value">
                      {(() => {
                        const date = new Date(user.vytvorene);
                        const dateStr = date.toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric' });
                        const timeStr = date.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' });
                        return `${dateStr} • ${timeStr}`;
                      })()}
                    </span>
                  </div>
                  <div className="user-mobile-detail full-width">
                    <span className="user-mobile-detail-label">Posledné prihlásenie</span>
                    <span className="user-mobile-detail-value">
                      {user.posledne_prihlasenie 
                        ? (() => {
                            const date = new Date(user.posledne_prihlasenie);
                            const dateStr = date.toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric' });
                            const timeStr = date.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' });
                            return `${dateStr} • ${timeStr}`;
                          })()
                        : 'N/A'}
                    </span>
                  </div>
                </div>
                
                {(user.can_edit || user.can_delete || user.id === currentAdmin.id) && (
                <div className="user-mobile-actions">
                  {(user.can_edit || user.id === currentAdmin.id) && (
                    <>
                      <button
                        className="btn-icon btn-warning"
                        onClick={() => handleEdit(user)}
                        title="Upraviť"
                      >
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.9888 4.28491L19.6405 2.93089C18.4045 1.6897 16.4944 1.6897 15.2584 2.93089L13.0112 5.30042L18.7416 11.055L21.1011 8.68547C21.6629 8.1213 22 7.33145 22 6.54161C22 5.75176 21.5506 4.84908 20.9888 4.28491Z"/>
                          <path d="M16.2697 10.9422L11.7753 6.42877L2.89888 15.3427C2.33708 15.9069 2 16.6968 2 17.5994V21.0973C2 21.5487 2.33708 22 2.89888 22H6.49438C7.2809 22 8.06742 21.6615 8.74157 21.0973L17.618 12.1834L16.2697 10.9422Z"/>
                        </svg>
                      </button>
                      {user.id !== currentAdmin.id && (
                        <button
                          className={`btn-icon ${user.aktivny ? 'btn-edit' : 'btn-success'}`}
                          onClick={() => toggleStatus(user)}
                          title={user.aktivny ? 'Deaktivovať' : 'Aktivovať'}
                        >
                          <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            {user.aktivny ? (
                              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z M5 6.5A1.5 1.5 0 0 1 6.5 5h3A1.5 1.5 0 0 1 11 6.5v3A1.5 1.5 0 0 1 9.5 11h-3A1.5 1.5 0 0 1 5 9.5v-3z"/>
                            ) : (
                              <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
                            )}
                          </svg>
                        </button>
                      )}
                      <button
                        className="btn-icon btn-info"
                        onClick={() => handleResetPassword(user)}
                        title="Reset hesla"
                      >
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
                        </svg>
                      </button>
                    </>
                  )}
                  {user.id === currentAdmin.id && (
                    <button
                      className="btn-icon btn-color"
                      onClick={() => handleChangeColor(user)}
                      title="Zmeniť farbu avatara"
                    >
                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M13.5 1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.499 2.499 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5zm-8.5 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm11 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"/>
                      </svg>
                    </button>
                  )}
                  {user.can_delete && user.id !== currentAdmin.id && (
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDeleteClick(user)}
                      title="Zmazať"
                    >
                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z"/>
                        <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3h11V2h-11v1Z"/>
                      </svg>
                    </button>
                  )}
                </div>
                )}
              </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay show" onClick={() => closeModalWithAnimation(() => setShowModal(false))}>
          <div className="modal-content modal-lg show" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'add' ? 'Pridať administrátora' : 'Upraviť administrátora'}</h2>
              <button className="modal-close" onClick={() => closeModalWithAnimation(() => setShowModal(false))}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {modalError && (
                  <div className="alert alert-error mb-3">
                    ✕ {modalError}
                  </div>
                )}

                <div className="form-grid-modern">
                  {/* Prvý riadok: Celé meno + Prihlasovacie meno */}
                  <div className={`form-group-floating ${formData.cele_meno ? 'has-value' : ''}`}>
                    <input
                      type="text"
                      name="cele_meno"
                      id="cele_meno"
                      className="form-input-floating"
                      value={formData.cele_meno}
                      onChange={(e) => setFormData({...formData, cele_meno: e.target.value})}
                      placeholder=" "
                      required
                    />
                    <label htmlFor="cele_meno" className="form-label-floating">Meno a priezvisko</label>
                  </div>

                  <div className={`form-group-floating ${formData.meno ? 'has-value' : ''}`}>
                    <input
                      type="text"
                      name="meno"
                      id="meno"
                      className="form-input-floating"
                      value={formData.meno}
                      onChange={(e) => setFormData({...formData, meno: e.target.value})}
                      placeholder=" "
                      required
                      disabled={modalMode === 'edit' && !isSuperAdmin}
                    />
                    <label htmlFor="meno" className="form-label-floating">Prihlasovacie meno</label>
                  </div>

                  {/* Druhý riadok: Email + Aktívny účet checkbox */}
                  <div className={`form-group-floating ${formData.email ? 'has-value' : ''}`}>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      className="form-input-floating"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder=" "
                      required
                      disabled={modalMode === 'edit' && !isSuperAdmin}
                    />
                    <label htmlFor="email" className="form-label-floating">Email</label>
                  </div>

                  {modalMode === 'edit' && (
                    <div className="form-group-checkbox-inline">
                      <label className="checkbox-label-inline">
                        <input
                          type="checkbox"
                          checked={formData.aktivny}
                          onChange={(e) => setFormData({...formData, aktivny: e.target.checked})}
                          disabled={editingUser?.id === currentAdmin.id}
                        />
                        <span>Aktivovať účet</span>
                      </label>
                    </div>
                  )}

                  {modalMode === 'add' && (
                    <>
                      {/* Heslo */}
                      <div className={`form-group-floating ${formData.heslo ? 'has-value' : ''}`}>
                        <input
                          type="password"
                          name="heslo"
                          id="heslo"
                          className="form-input-floating"
                          value={formData.heslo}
                          onChange={(e) => setFormData({...formData, heslo: e.target.value})}
                          placeholder=" "
                          minLength={8}
                          required
                        />
                        <label htmlFor="heslo" className="form-label-floating">Heslo (min. 8 znakov)</label>
                      </div>

                      {/* Rola */}
                      <div className={`form-group-floating ${formData.rola ? 'has-value' : ''}`}>
                        <select
                          name="rola"
                          id="rola"
                          className="form-input-floating"
                          value={formData.rola}
                          onChange={(e) => setFormData({...formData, rola: e.target.value})}
                          required
                        >
                          <option value="admin">Pracovník</option>
                          <option value="super_admin">Šéf</option>
                        </select>
                        <label htmlFor="rola" className="form-label-floating">Rola</label>
                      </div>
                    </>
                  )}
                </div>

              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => closeModalWithAnimation(() => setShowModal(false))}
                  className="btn btn-secondary"
                >
                  Zrušiť
                </button>
                <button type="submit" className="btn btn-primary">
                  {modalMode === 'add' ? 'Vytvoriť administrátora' : 'Uložiť zmeny'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showResetPasswordModal && (
        <div className="modal-overlay show" onClick={() => closeModalWithAnimation(() => setShowResetPasswordModal(false))}>
          <div className="modal-content modal-md show" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reset hesla</h2>
              <button className="modal-close" onClick={() => closeModalWithAnimation(() => setShowResetPasswordModal(false))}>×</button>
            </div>

            <form onSubmit={handleResetPasswordSubmit}>
              <div className="modal-body">
                {resetPasswordError && (
                  <div className="alert alert-error mb-3">
                    ✕ {resetPasswordError}
                  </div>
                )}

                <div className="alert alert-info mb-4">
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16" style={{marginRight: '0.5rem', verticalAlign: 'middle'}}>
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                  </svg>
                  Nastavte nové heslo (min. 8 znakov) pre&nbsp;<strong>{editingUser?.meno}</strong>
                </div>

                <div className="form-grid-modern">
                  {/* Nové heslo */}
                  <div className={`form-group-floating ${resetPasswordData.heslo ? 'has-value' : ''}`}>
                    <input
                      type="password"
                      name="heslo"
                      id="reset_heslo"
                      className="form-input-floating"
                      value={resetPasswordData.heslo}
                      onChange={(e) => setResetPasswordData({...resetPasswordData, heslo: e.target.value})}
                      placeholder=" "
                      minLength={8}
                      required
                    />
                    <label htmlFor="reset_heslo" className="form-label-floating">Nové heslo</label>
                  </div>

                  {/* Potvrdiť heslo */}
                  <div className={`form-group-floating ${resetPasswordData.heslo_confirm ? 'has-value' : ''}`}>
                    <input
                      type="password"
                      name="heslo_confirm"
                      id="reset_heslo_confirm"
                      className="form-input-floating"
                      value={resetPasswordData.heslo_confirm}
                      onChange={(e) => setResetPasswordData({...resetPasswordData, heslo_confirm: e.target.value})}
                      placeholder=" "
                      minLength={8}
                      required
                    />
                    <label htmlFor="reset_heslo_confirm" className="form-label-floating">Potvrdiť heslo</label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => closeModalWithAnimation(() => setShowResetPasswordModal(false))}
                  className="btn btn-secondary"
                >
                  Zrušiť
                </button>
                <button type="submit" className="btn btn-primary">
                  Zmeniť heslo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfirmDelete && (
        <ConfirmModal
          show={showConfirmDelete}
          title="Zmazať administrátora"
          message={`Naozaj chcete zmazať administrátora "${userToDelete?.meno}"? Táto akcia je nenávratná.`}
          confirmText="Zmazať"
          confirmButtonClass="btn-danger"
          onConfirm={handleConfirmDelete}
          onClose={() => setShowConfirmDelete(false)}
        />
      )}

      {showColorModal && (
        <div className="modal-overlay show" onClick={() => closeModalWithAnimation(() => setShowColorModal(false))}>
          <div className="modal-content modal-md show" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Zmeniť farbu avatara</h2>
              <button className="modal-close" onClick={() => closeModalWithAnimation(() => setShowColorModal(false))}>×</button>
            </div>
            <div className="modal-body">
              <p className="mb-1 text-center" style={{color: '#6b7280'}}>
                Vyberte farbu pre avatar používateľa <strong>{colorModalUser?.cele_meno || colorModalUser?.meno}</strong>.
              </p>
              <div className="color-picker-grid">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((colorIndex) => (
                  <button
                    key={colorIndex}
                    className={`color-picker-option admin-avatar-gradient-${colorIndex} ${
                      (colorModalUser?.avatar_color !== null && colorModalUser?.avatar_color !== undefined 
                        ? colorModalUser.avatar_color 
                        : colorModalUser?.id % 10) === colorIndex ? 'selected' : ''
                    }`}
                    onClick={() => handleColorSelect(colorIndex)}
                    title={`Farba ${colorIndex + 1}`}
                  >
                    <div className="color-preview-initials">
                      {colorModalUser?.cele_meno?.substring(0, 2).toUpperCase() || '??'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
